import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { ElementType } from 'api/graphQL/reactiveVars/element'
import React from 'react'
import { useParams } from 'react-router-dom'
import { useElementItemShallowSelector } from '../ElementItemStore'
import { useSuggestionsShallowSelector } from '../SuggestionStore'
import { useShallowSelector } from '../Workspace2Store'
import { usePTProjectFilterCache } from '../features/ptActivation/hooks'
import { usePTCacheStoreShallowSelector } from '../features/ptActivation/store'

/**
 * Props for the `useElementToTreeMapper` hook.
 *
 * @interface ElementToTreeMapperProps
 * @property {boolean} isElementsLibraryPage - Indicates if the current page is the elements library page.
 * @property {boolean} isSelected - Indicates if the element is selected.
 * @property {string} elementId - The ID of the element.
 * @property {string} baseElementId - The base ID of the element.
 * @property {string} parentFamilyId - The ID of the parent family.
 * @property {string} elementType - The type of the element.
 * @property {string | null | undefined} [elementAddedToProjectId] - The ID of the element added to the project, if applicable.
 * @property {string | undefined} [selectedElementFormat] - The element format (DIVISIONS, FAMILIES, etc.) for determining isProductFamilyGroupView.
 */
interface ElementToTreeMapperProps {
    isElementsLibraryPage: boolean
    isSelected: boolean
    elementId: string
    baseElementId: string
    parentFamilyId: string
    elementType: string
    elementAddedToProjectId?: string | null
    selectedElementFormat?: string
}

/**
 * A custom hook that maps an element to the tree structure and performs related actions.
 *
 * @param {ElementToTreeMapperProps} props - The properties required for mapping the element to the tree.
 * @returns {void}
 *
 * @description
 * This hook is responsible for:
 * - Updating the element item value in the store when the element is selected and not on the elements library page.
 * - Resetting track changes data and removing all suggestion data.
 * - Handling suggestion card filter changes.
 * - Setting the tree loading state.
 *
 * @example
 * const props = {
 *   isElementsLibraryPage: false,
 *   isSelected: true,
 *   elementId: '123',
 *   baseElementId: '456',
 *   parentFamilyId: '789',
 *   elementType: 'ProductType',
 *   elementAddedToProjectId: null,
 * };
 * useElementToTreeMapper(props);
 */
export const useElementToTreeMapper = (props: ElementToTreeMapperProps) => {
    const {
        isElementsLibraryPage,
        isSelected,
        elementId,
        baseElementId,
        parentFamilyId,
        elementType,
        elementAddedToProjectId,
        selectedElementFormat,
    } = props

    const {
        elementItemValue,
        setElementItemValue,
        setSelectedElementFormat,
    } = useElementItemShallowSelector((state) => ({
        elementItemValue: state.elementItemValue,
        setElementItemValue: state.setElementItemValue,
        setSelectedElementFormat: state.setSelectedElementFormat,
    }))
    const {
        setIsSuggestionsDataUpdated,
        removeAllSuggestionData,
        setTrackChangesData,
    } = useSuggestionsShallowSelector((state) => ({
        setIsSuggestionsDataUpdated: state.setIsSuggestionsDataUpdated,
        removeAllSuggestionData: state.removeAllSuggestionData,
        setTrackChangesData: state.setTrackChangesData,
    }))
    const {
        setIsTreeLoading,
        setTreeData,
        setContentItemViewNodes,
    } = useShallowSelector((state) => ({
        setIsTreeLoading: state.actions.setIsTreeLoading,
        setTreeData: state.actions.setTreeData,
        setContentItemViewNodes: state.actions.setContentItemViewNodes,
    }))

    // URL is authoritative: store projectId lags during the unmount→remount window between projects.
    const { projectId } = useParams<{ projectId?: string }>()

    // PT Cache for handling "Added To Project" filter
    const { fetchAndCacheFamilyPTs } = usePTProjectFilterCache()
    const { clearAllCache } = usePTCacheStoreShallowSelector((state) => ({
        clearAllCache: state.clearAllCache,
    }))

    const shouldUpdateElement = React.useMemo(() => {
        const alreadySelected = elementItemValue.baseElementId === baseElementId
        return !isElementsLibraryPage && isSelected && !alreadySelected
    }, [
        isElementsLibraryPage,
        isSelected,
        elementItemValue.baseElementId,
        baseElementId,
    ])

    const newElementValue = React.useMemo(
        () => ({
            id: elementId,
            elementId: elementAddedToProjectId || baseElementId,
            parentFamilyId,
            elementType,
            isProductTypeProject: elementType === ElementType.PRODUCTTYPE,
            isProjectProductFamily: Boolean(elementAddedToProjectId),
            actionFlag: elementType,
            baseElementId,
            // Store current family ID for efficient PT cache lookups:
            // - When FAMILY selected: use elementId (family's own ID)
            // - When PT selected: use parentFamilyId (PT's parent family)
            // - Otherwise: use parentFamilyId as fallback
            currentFamilyId:
                elementType === ProjectElementType.PRODUCTFAMILY
                    ? elementId
                    : parentFamilyId,
        }),
        [
            elementId,
            elementAddedToProjectId,
            baseElementId,
            parentFamilyId,
            elementType,
        ]
    )

    React.useEffect(() => {
        if (!shouldUpdateElement) return

        const resetTreeAndContent = () => {
            setTreeData([])
            setContentItemViewNodes([])
        }

        const resetSuggestionData = () => {
            removeAllSuggestionData()
            setTrackChangesData({}, [], true) // Clear all track changes data
        }

        resetTreeAndContent()
        resetSuggestionData()
        setIsTreeLoading(true)
        setElementItemValue(newElementValue)

        // Set the selected element format in the store
        if (selectedElementFormat && setSelectedElementFormat) {
            setSelectedElementFormat(selectedElementFormat)
        }

        setIsSuggestionsDataUpdated(true)
    }, [
        shouldUpdateElement,
        newElementValue,
        setElementItemValue,
        removeAllSuggestionData,
        setIsSuggestionsDataUpdated,
        setIsTreeLoading,
        setTrackChangesData,
        setTreeData,
        setContentItemViewNodes,
        selectedElementFormat,
        setSelectedElementFormat,
    ])

    // Cache Product Types when FAMILY is selected
    // By proactively caching ALL PTs for the family, we ensure PT activation can access
    // inactive PTs even when they're not in the filtered elementList
    React.useEffect(() => {
        if (
            shouldUpdateElement &&
            elementType === ProjectElementType.PRODUCTFAMILY &&
            elementId &&
            projectId
        ) {
            clearAllCache() // Clear cache when switching families to prevent stale data
            // Fetch and cache ALL PTs for this family (ignoring filter)
            fetchAndCacheFamilyPTs(elementId, projectId).catch(() => {})
        }
    }, [
        shouldUpdateElement,
        elementType,
        baseElementId,
        elementId,
        projectId,
        clearAllCache,
        fetchAndCacheFamilyPTs,
    ])
}
